import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '../../API';
import { girderThumbnailUrl } from '../../utils/girderThumbnailUrl';
import Tooltip from '../../components/Tooltip';
import { PopoverShell } from '../../components/PopoverShell/PopoverShell';
import { NotesPopoverBody } from '../../components/NotesControl';
import { collectAnnotationIds } from './progress/geojsonExportSelection';
import { parseProgressExportCsv } from './progress/parseProgressExportCsv';
import { getQuestionDisplayName } from './uis/SlideLevelClassificationUI/slideLevelClassificationModel';
import styles from './ProjectProgressPage.module.css';

function statusBadge(status) {
    if (status === 'completed') {
        return <span className={`${styles.badge} ${styles.badgeCompleted}`}>Completed</span>;
    }
    if (status === 'in_progress') {
        return <span className={`${styles.badge} ${styles.badgeInProgress}`}>In progress</span>;
    }
    return <span className={`${styles.badge} ${styles.badgeNotStarted}`}>Not started</span>;
}

export default function ProjectProgressPage() {
    const { folderId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [projectName, setProjectName] = useState('');
    const [notesConfig, setNotesConfig] = useState(null);
    const [items, setItems] = useState([]);
    const [users, setUsers] = useState([]);
    const [previewFormat, setPreviewFormat] = useState(null);
    const [previewText, setPreviewText] = useState('');
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [geojsonPreviewShape, setGeojsonPreviewShape] = useState('single');
    const [geojsonPreviewScopeLabel, setGeojsonPreviewScopeLabel] = useState('');
    /** @type {{ annotationUIId: string | null, layouts: string[] } | null} */
    const [exportCapabilities, setExportCapabilities] = useState(null);
    /** SLC questions from project config (for wide export picker). */
    const [slcQuestions, setSlcQuestions] = useState([]);
    /** @type {{ surveys: object[], byUser: Record<string, Record<string, string>>, exportLayouts?: string[] } | null} */
    const [surveyProgress, setSurveyProgress] = useState(null);
    /** Export scope: 'items' or a survey type (pre_test | general | post_test). */
    const [exportScope, setExportScope] = useState('items');
    const [previewQuestionId, setPreviewQuestionId] = useState(null);
    const [previewQuestionLabel, setPreviewQuestionLabel] = useState('');

    const closeTimerRef = useRef(null);
    const wideCloseTimerRef = useRef(null);
    const [geojsonMenu, setGeojsonMenu] = useState({
        open: false,
        scope: null,
        anchorRect: null,
        pinned: false,
    });
    const [wideMenu, setWideMenu] = useState({
        open: false,
        anchorRect: null,
        pinned: false,
    });

    // Notes preview popover (admin read-only). Keyed by (userId, itemId) so a
    // second click on the same glyph closes it; clicking a different glyph
    // moves the popover to that anchor.
    const [notesPopover, setNotesPopover] = useState({
        open: false,
        anchorRect: null,
        userId: null,
        itemId: null,
        value: '',
        byUser: '',
    });
    const notesPopoverReactId = useId();
    const notesPopoverTitleId = `progress-notes-title-${notesPopoverReactId}`;
    const notesPopoverDescId = `progress-notes-desc-${notesPopoverReactId}`;

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        Promise.all([
            api.get(`projects/${folderId}`, {}),
            api.get(`projects/${folderId}/progress/all`, {}),
        ]).then(([projRes, progRes]) => {
            if (cancelled) return;
            if (projRes.status === 200) {
                setProjectName(projRes.data?.name || '');
                setNotesConfig(projRes.data?.girderProjects?.notes ?? null);
                const ui = projRes.data?.girderProjects?.annotationUI;
                const qs = ui?.id === 'slide-level-classification' && Array.isArray(ui?.config?.questions)
                    ? ui.config.questions.filter((q) => q && (q.id != null && String(q.id).trim()))
                    : [];
                setSlcQuestions(qs);
            }
            if (progRes.status === 200) {
                setItems(progRes.data?.items || []);
                setUsers(progRes.data?.users || []);
                setExportCapabilities(progRes.data?.exportCapabilities ?? null);
                setSurveyProgress(progRes.data?.surveyProgress ?? null);
            } else {
                setError(progRes.data?.message || 'Failed to load progress.');
            }
        }).catch((e) => {
            if (!cancelled) setError(e.message);
        }).finally(() => {
            if (!cancelled) setLoading(false);
        });

        return () => { cancelled = true; };
    }, [folderId]);

    const itemIds = useMemo(() => items.map((it) => String(it.itemId)), [items]);

    const progressPayload = useMemo(() => ({ items, users }), [items, users]);

    // Map (userId, itemId) -> status for cell lookup
    const statusByUserItem = useMemo(() => {
        const map = {};
        users.forEach((u) => {
            (u.itemProgress || []).forEach((p) => {
                const key = `${u.userId}:${String(p.itemId)}`;
                map[key] = p?.status || 'not_started';
            });
        });
        return map;
    }, [users]);

    // Map (userId, itemId) -> notes string. Empty/missing notes are absent from
    // the map so the boolean check `Boolean(notes)` doubles as the "has notes"
    // signal that controls the glyph's visibility.
    const notesByUserItem = useMemo(() => {
        const map = {};
        users.forEach((u) => {
            (u.itemProgress || []).forEach((p) => {
                const notes = typeof p?.notes === 'string' ? p.notes : '';
                if (notes.trim().length > 0) {
                    map[`${u.userId}:${String(p.itemId)}`] = notes;
                }
            });
        });
        return map;
    }, [users]);

    // Per-user totals: { userId: { completed, inProgress, notStarted, total } }
    const userTotals = useMemo(() => {
        const totals = {};
        users.forEach((u) => {
            let completed = 0;
            let inProgress = 0;
            let notStarted = 0;
            itemIds.forEach((iid) => {
                const s = statusByUserItem[`${u.userId}:${iid}`] || 'not_started';
                if (s === 'completed') completed += 1;
                else if (s === 'in_progress') inProgress += 1;
                else notStarted += 1;
            });
            totals[u.userId] = { completed, inProgress, notStarted, total: itemIds.length };
        });
        return totals;
    }, [users, itemIds, statusByUserItem]);

    // Per-item totals: { itemId: { completed, inProgress, notStarted, total } }
    const itemTotals = useMemo(() => {
        const totals = {};
        itemIds.forEach((iid) => {
            let completed = 0;
            let inProgress = 0;
            let notStarted = 0;
            users.forEach((u) => {
                const s = statusByUserItem[`${u.userId}:${iid}`] || 'not_started';
                if (s === 'completed') completed += 1;
                else if (s === 'in_progress') inProgress += 1;
                else notStarted += 1;
            });
            totals[iid] = { completed, inProgress, notStarted, total: users.length };
        });
        return totals;
    }, [users, itemIds, statusByUserItem]);

    function userLabel(u) {
        return u.displayName || u.login || u.userId;
    }

    function userSummary(userId) {
        const t = userTotals[userId];
        if (!t || t.total === 0) return '—';
        return `${t.completed}/${t.total} completed`;
    }

    function itemSummary(itemId) {
        const t = itemTotals[itemId];
        if (!t || t.total === 0) return '—';
        return `${t.completed}/${t.total} completed`;
    }

    function handleThumbnailClick(itemId) {
        if (!itemId) return;
        navigate(`/projects/${folderId}/review/${itemId}`);
    }

    const configuredSurveys = Array.isArray(surveyProgress?.surveys)
        ? surveyProgress.surveys
        : [];
    const surveyByUser = surveyProgress?.byUser && typeof surveyProgress.byUser === 'object'
        ? surveyProgress.byUser
        : {};

    const scopeIsItems = exportScope === 'items';
    const itemExportLayouts = Array.isArray(exportCapabilities?.layouts)
        ? exportCapabilities.layouts
        : [];
    const surveyExportLayouts = Array.isArray(surveyProgress?.exportLayouts)
        ? surveyProgress.exportLayouts
        : ['long', 'wide', 'json'];
    const activeLayouts = scopeIsItems ? itemExportLayouts : surveyExportLayouts;
    const canExportLong = activeLayouts.includes('long');
    const canExportWide = activeLayouts.includes('wide');
    const canExportJson = activeLayouts.includes('json');
    const canExportAny = canExportLong || canExportWide || canExportJson;
    const isSlcProject = exportCapabilities?.annotationUIId === 'slide-level-classification';
    const canExportGeojson = scopeIsItems && Boolean(exportCapabilities) && !isSlcProject;
    const slcNeedsWidePicker = scopeIsItems && isSlcProject && slcQuestions.length > 1;

    const exportTypeLabel = (type) => {
        if (type === 'long') return 'Long CSV';
        if (type === 'wide') return 'Wide CSV';
        return 'JSON';
    };

    function surveyScopeLabel(surveyType) {
        const s = configuredSurveys.find((x) => x.type === surveyType);
        return (s && s.label) || surveyType;
    }

    function surveySummary(surveyType) {
        if (!users.length) return '—';
        let completed = 0;
        users.forEach((u) => {
            const st = surveyByUser[String(u.userId)]?.[surveyType] || 'not_started';
            if (st === 'completed') completed += 1;
        });
        return `${completed}/${users.length} completed`;
    }

    function handlePreview(exportType, questionId = null, questionLabel = '') {
        setPreviewError(null);
        setPreviewText('');
        setPreviewLoading(true);
        setPreviewFormat(exportType);
        setPreviewQuestionId(questionId || null);
        setPreviewQuestionLabel(questionLabel || '');
        const isJson = exportType === 'json';

        if (!scopeIsItems) {
            api.get(`projects/${folderId}/surveys/${exportScope}/export`, {
                query: { layout: exportType },
                responseType: isJson ? 'json' : 'text',
            })
                .then((res) => {
                    if (res.status !== 200) {
                        setPreviewError(res.data?.message || `Export failed (${res.status}).`);
                        return;
                    }
                    const text = isJson ? JSON.stringify(res.data, null, 2) : res.data;
                    setPreviewText(typeof text === 'string' ? text : String(text ?? ''));
                })
                .catch((e) => setPreviewError(e?.message || String(e)))
                .finally(() => setPreviewLoading(false));
            return;
        }

        const query = { layout: exportType };
        if (questionId) query.questionId = questionId;
        api.get(`projects/${folderId}/progress/export`, {
            query,
            responseType: isJson ? 'json' : 'text',
        })
            .then((res) => {
                if (res.status !== 200) {
                    const msg = res.data?.message || `Export failed (${res.status}).`;
                    setPreviewError(msg);
                    return;
                }
                const text = isJson ? JSON.stringify(res.data, null, 2) : res.data;
                setPreviewText(text);
            })
            .catch((e) => setPreviewError(e?.message || String(e)))
            .finally(() => setPreviewLoading(false));
    }

    function handleDownload(exportType, questionId = null) {
        const safeName = (projectName || 'project').replace(/[^a-z0-9-_]+/gi, '_');

        if (!scopeIsItems) {
            const stype = exportScope;
            api.get(`projects/${folderId}/surveys/${stype}/export`, {
                query: { layout: exportType },
                responseType: 'blob',
            })
                .then((res) => {
                    if (res.status !== 200) {
                        setError(res.data?.message || `Export failed (${res.status}).`);
                        return;
                    }
                    const blob = res.data;
                    const a = document.createElement('a');
                    const objectUrl = URL.createObjectURL(blob);
                    a.href = objectUrl;
                    const safeType = String(stype).replace(/[^a-z0-9-_]+/gi, '_');
                    const ext = exportType === 'json' ? 'json' : 'csv';
                    const layoutSuffix = exportType === 'wide' ? '-wide' : exportType === 'long' ? '-long' : '';
                    a.download = `${safeName}-survey-${safeType}${layoutSuffix}.${ext}`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    URL.revokeObjectURL(objectUrl);
                })
                .catch((e) => setError(e?.message || String(e)));
            return;
        }

        const qid = questionId || previewQuestionId;
        const query = { layout: exportType };
        if (qid) query.questionId = qid;
        api.get(`projects/${folderId}/progress/export`, {
            query,
            responseType: 'blob',
        })
            .then((res) => {
                if (res.status !== 200) {
                    const msg = res.data?.message || `Export failed (${res.status}).`;
                    setError(msg);
                    return;
                }
                const blob = res.data;
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                const ext = exportType === 'json' ? 'json' : 'csv';
                let suffix = '';
                if (exportType === 'long') suffix = '-long';
                else if (exportType === 'wide') {
                    const safeQ = qid
                        ? String(qid).replace(/[^a-z0-9-_]+/gi, '_').slice(0, 80)
                        : '';
                    suffix = safeQ ? `-wide-${safeQ}` : '-wide';
                }
                a.download = `${safeName}-progress${suffix}.${ext}`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(objectUrl);
            })
            .catch((e) => setError(e?.message || String(e)));
    }

    function handleExportScopeChange(nextScope) {
        setExportScope(nextScope);
        setPreviewText('');
        setPreviewFormat(null);
        setPreviewError(null);
        setPreviewQuestionId(null);
        setPreviewQuestionLabel('');
    }

    function scopeLabel(scope) {
        if (!scope) return '';
        if (scope.type === 'all') return 'All annotations';
        if (scope.type === 'row') {
            const iid = String(scope.itemId);
            const itemName = items.find((it) => String(it.itemId) === iid)?.name;
            return `${itemName || `Item ${iid}`} (all users)`;
        }
        if (scope.type === 'col') {
            const uid = String(scope.userId);
            const u = users.find((x) => String(x.userId) === uid);
            return `${u ? userLabel(u) : `User ${uid}`} (all items)`;
        }
        if (scope.type === 'cell') {
            const iid = String(scope.itemId);
            const uid = String(scope.userId);
            const itemName = items.find((it) => String(it.itemId) === iid)?.name;
            const u = users.find((x) => String(x.userId) === uid);
            return `${itemName || `Item ${iid}`} × ${u ? userLabel(u) : `User ${uid}`}`;
        }
        return '';
    }

    function clearGeojsonCloseTimer() {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current);
            closeTimerRef.current = null;
        }
    }

    function scheduleGeojsonClose() {
        clearGeojsonCloseTimer();
        closeTimerRef.current = setTimeout(() => {
            setGeojsonMenu((s) => (s.pinned ? s : { ...s, open: false, scope: null, anchorRect: null }));
        }, 160);
    }

    function openGeojsonMenuHover(e, scope) {
        const rect = e.currentTarget?.getBoundingClientRect?.();
        if (!rect) return;
        clearGeojsonCloseTimer();
        setGeojsonMenu((s) => ({
            ...s,
            open: true,
            pinned: false,
            scope,
            anchorRect: rect,
        }));
    }

    function toggleGeojsonMenuPinned(e, scope) {
        const rect = e.currentTarget?.getBoundingClientRect?.();
        if (!rect) return;
        clearGeojsonCloseTimer();
        setGeojsonMenu((s) => {
            const sameScope = JSON.stringify(s.scope) === JSON.stringify(scope);
            if (s.open && s.pinned && sameScope) {
                return { ...s, open: false, pinned: false, scope: null, anchorRect: null };
            }
            return { ...s, open: true, pinned: true, scope, anchorRect: rect };
        });
    }

    function closeGeojsonMenu() {
        clearGeojsonCloseTimer();
        setGeojsonMenu((s) => ({ ...s, open: false, scope: null }));
    }

    function clearWideCloseTimer() {
        if (wideCloseTimerRef.current) {
            clearTimeout(wideCloseTimerRef.current);
            wideCloseTimerRef.current = null;
        }
    }

    function scheduleWideClose() {
        clearWideCloseTimer();
        wideCloseTimerRef.current = setTimeout(() => {
            setWideMenu((s) => (s.pinned ? s : { ...s, open: false, anchorRect: null }));
        }, 160);
    }

    function openWideMenuHover(e) {
        const rect = e.currentTarget?.getBoundingClientRect?.();
        if (!rect) return;
        clearWideCloseTimer();
        setWideMenu((s) => ({
            ...s,
            open: true,
            pinned: false,
            anchorRect: rect,
        }));
    }

    function toggleWideMenuPinned(e) {
        const rect = e.currentTarget?.getBoundingClientRect?.();
        if (!rect) return;
        clearWideCloseTimer();
        setWideMenu((s) => {
            if (s.open && s.pinned) {
                return { ...s, open: false, pinned: false, anchorRect: null };
            }
            return { ...s, open: true, pinned: true, anchorRect: rect };
        });
    }

    function closeWideMenu() {
        clearWideCloseTimer();
        setWideMenu((s) => ({ ...s, open: false }));
    }

    function startWideExportForQuestion(q, qIndex) {
        const qid = String(q.id).trim();
        const label = getQuestionDisplayName(q, qIndex);
        closeWideMenu();
        handlePreview('wide', qid, label);
    }

    function startWideExportDefault() {
        if (scopeIsItems && isSlcProject && slcQuestions.length >= 1) {
            startWideExportForQuestion(slcQuestions[0], 0);
            return;
        }
        handlePreview('wide');
    }

    // Open or move the notes preview popover. We deliberately do not implement
    // toggle-on-second-click here: PopoverShell's document mousedown handler
    // (outside-click close) runs before the button's click event, which would
    // race with a toggle and leave the popover stuck open. Clicking another
    // glyph re-targets the popover; clicking outside or pressing Escape closes
    // it (handled by PopoverShell while pinned=false).
    function openNotesPopover(e, { userId, itemId, value, byUser }) {
        const rect = e.currentTarget?.getBoundingClientRect?.();
        if (!rect) return;
        setNotesPopover({
            open: true,
            anchorRect: rect,
            userId,
            itemId,
            value,
            byUser,
        });
    }

    function closeNotesPopover() {
        setNotesPopover((s) => ({ ...s, open: false }));
    }

    function safeName(s) {
        return String(s || 'project').replace(/[^a-z0-9-_]+/gi, '_');
    }

    function geojsonFileName(scope, responseShape) {
        const base = safeName(projectName || 'project');
        const shapeSuffix = responseShape === 'perAnnotation' ? 'perAnnotation' : 'single';
        if (!scope) return `${base}-geojson-${shapeSuffix}.geojson`;
        if (scope.type === 'all') return `${base}-geojson-all-${shapeSuffix}.geojson`;
        if (scope.type === 'row') {
            const iid = String(scope.itemId);
            const itemName = items.find((it) => String(it.itemId) === iid)?.name;
            return `${base}-geojson-item_${safeName(itemName || iid)}-${shapeSuffix}.geojson`;
        }
        if (scope.type === 'col') {
            const uid = String(scope.userId);
            const u = users.find((x) => String(x.userId) === uid);
            return `${base}-geojson-user_${safeName((u && userLabel(u)) || uid)}-${shapeSuffix}.geojson`;
        }
        if (scope.type === 'cell') {
            const iid = String(scope.itemId);
            const uid = String(scope.userId);
            const itemName = items.find((it) => String(it.itemId) === iid)?.name;
            const u = users.find((x) => String(x.userId) === uid);
            return `${base}-geojson-item_${safeName(itemName || iid)}-user_${safeName((u && userLabel(u)) || uid)}-${shapeSuffix}.geojson`;
        }
        return `${base}-geojson-${shapeSuffix}.geojson`;
    }

    function postGeojson({ annotationIds, responseShape, responseType }) {
        return api.post(`projects/${folderId}/annotations/geojson`, {
            json: {
                annotationIds,
                responseShape,
                merge: true,
                includeEmpty: false,
            },
            responseType,
        });
    }

    function handleGeojsonPreview(scope, responseShape) {
        const annotationIds = collectAnnotationIds(progressPayload, scope);
        if (!annotationIds.length) return;
        setPreviewError(null);
        setPreviewText('');
        setPreviewLoading(true);
        setPreviewFormat('geojson');
        setGeojsonPreviewShape(responseShape);
        setGeojsonPreviewScopeLabel(scopeLabel(scope));
        postGeojson({ annotationIds, responseShape, responseType: 'json' })
            .then((res) => {
                if (res.status !== 200) {
                    if (res.status === 404) {
                        setPreviewError(res.data?.message || 'No saved annotations to export for this selection.');
                        return;
                    }
                    const msg = res.data?.message || `Export failed (${res.status}).`;
                    setPreviewError(msg);
                    return;
                }
                setPreviewText(JSON.stringify(res.data, null, 2));
            })
            .catch((e) => setPreviewError(e?.message || String(e)))
            .finally(() => setPreviewLoading(false));
    }

    function handleGeojsonDownload(scope, responseShape) {
        const annotationIds = collectAnnotationIds(progressPayload, scope);
        if (!annotationIds.length) return;
        postGeojson({ annotationIds, responseShape, responseType: 'blob' })
            .then((res) => {
                if (res.status !== 200) {
                    if (res.status === 404) {
                        setError(res.data?.message || 'No saved annotations to export for this selection.');
                        return;
                    }
                    const msg = res.data?.message || `Export failed (${res.status}).`;
                    setError(msg);
                    return;
                }
                const blob = res.data;
                const a = document.createElement('a');
                const objectUrl = URL.createObjectURL(blob);
                a.href = objectUrl;
                a.download = geojsonFileName(scope, responseShape);
                document.body.appendChild(a);
                a.click();
                a.remove();
                URL.revokeObjectURL(objectUrl);
            })
            .catch((e) => setError(e?.message || String(e)));
    }

    if (loading) return <div className={styles.loading}>Loading progress…</div>;

    if (error) {
        return (
            <div className={styles.page}>
                <div className={styles.errorMessage}>{error}</div>
                <Link to={`/projects/${folderId}`}>← Back to project</Link>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            <div className={styles.headerRow}>
                <div>
                    <h3 className={styles.title}>Progress</h3>
                    <p className={styles.subTitle}>
                        <Link to={`/projects/${folderId}`}>← {projectName || 'Back to project'}</Link>
                    </p>
                </div>
                <div className={styles.actions}>
                    <span className={styles.actionsLabel}>View and Download:</span>
                    <label className={styles.scopeLabel} htmlFor="progress-export-scope">
                        <span className={styles.srOnly}>Export scope</span>
                        <select
                            id="progress-export-scope"
                            className={styles.scopeSelect}
                            value={exportScope}
                            onChange={(e) => handleExportScopeChange(e.target.value)}
                        >
                            <option value="items">Items</option>
                            {configuredSurveys.map((s) => (
                                <option key={s.type} value={s.type}>
                                    {surveyScopeLabel(s.type)}
                                </option>
                            ))}
                        </select>
                    </label>
                    {canExportGeojson ? (
                        <button
                            type="button"
                            className={styles.buttonSecondary}
                            onMouseEnter={(e) => openGeojsonMenuHover(e, { type: 'all' })}
                            onMouseLeave={scheduleGeojsonClose}
                            onClick={(e) => toggleGeojsonMenuPinned(e, { type: 'all' })}
                            disabled={users.length === 0 || items.length === 0}
                            title="Export GeoJSON (all annotations in this project)"
                        >
                            GeoJSON
                        </button>
                    ) : null}
                    {canExportLong && (
                        <button
                            type="button"
                            className={styles.buttonSecondary}
                            onClick={() => handlePreview('long')}
                            disabled={previewLoading}
                        >
                            {previewLoading && previewFormat === 'long' ? 'Loading…' : 'Long CSV'}
                        </button>
                    )}
                    {canExportWide && (
                        slcNeedsWidePicker ? (
                            <button
                                type="button"
                                className={styles.buttonSecondary}
                                onMouseEnter={openWideMenuHover}
                                onMouseLeave={scheduleWideClose}
                                onClick={toggleWideMenuPinned}
                                disabled={previewLoading || slcQuestions.length === 0}
                                title="Wide CSV — one question at a time (cases × participants)"
                            >
                                {previewLoading && previewFormat === 'wide' ? 'Loading…' : 'Wide CSV'}
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={styles.buttonSecondary}
                                onClick={startWideExportDefault}
                                disabled={previewLoading}
                                title={scopeIsItems
                                    ? (isSlcProject
                                        ? 'Wide CSV — cases × participants'
                                        : 'Wide CSV')
                                    : 'Wide CSV — participants × questions'}
                            >
                                {previewLoading && previewFormat === 'wide' ? 'Loading…' : 'Wide CSV'}
                            </button>
                        )
                    )}
                    {canExportJson && (
                        <button
                            type="button"
                            className={styles.buttonSecondary}
                            onClick={() => handlePreview('json')}
                            disabled={previewLoading}
                        >
                            {previewLoading && previewFormat === 'json' ? 'Loading…' : 'JSON'}
                        </button>
                    )}
                    {!canExportAny && (
                        <span className={styles.muted}>
                            {scopeIsItems
                                ? 'Progress export is not available for this project\'s annotation UI.'
                                : 'Survey export is not available.'}
                        </span>
                    )}
                </div>
            </div>

            {previewError && (
                <div className={styles.errorMessage}>{previewError}</div>
            )}

            {(previewText || previewLoading) && (
                <div className={styles.exportPreview}>
                    <div className={styles.exportPreviewHeader}>
                        <span className={styles.exportPreviewTitle}>
                            Export preview{' '}
                            {previewFormat === 'geojson'
                                ? `(GeoJSON — ${geojsonPreviewScopeLabel || 'selection'} — ${geojsonPreviewShape})`
                                : previewFormat === 'wide' && scopeIsItems && previewQuestionLabel
                                  ? `(Wide CSV — ${previewQuestionLabel})`
                                  : previewFormat
                                    ? `(${exportTypeLabel(previewFormat)}${
                                        !scopeIsItems ? ` — ${surveyScopeLabel(exportScope)}` : ''
                                    })`
                                    : ''}
                        </span>
                        {previewText && previewFormat && (
                            <button
                                type="button"
                                className={styles.buttonSecondary}
                                onClick={() =>
                                    previewFormat === 'geojson'
                                        ? handleGeojsonDownload(geojsonMenu.scope || { type: 'all' }, geojsonPreviewShape)
                                        : handleDownload(previewFormat)
                                }
                            >
                                Download {previewFormat === 'geojson' ? 'GeoJSON' : exportTypeLabel(previewFormat)}
                            </button>
                        )}
                    </div>
                    {previewLoading ? (
                        <div className={styles.exportPreviewLoading}>Loading export data…</div>
                    ) : (previewFormat === 'long' || previewFormat === 'wide') ? (() => {
                        const { headers, rows } = parseProgressExportCsv(previewText);
                        return (
                            <div className={styles.exportPreviewTableWrap}>
                                <table className={styles.exportPreviewTable}>
                                    <thead>
                                        <tr>
                                            {headers.map((h, i) => (
                                                <th key={i}>{h}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, ri) => (
                                            <tr key={ri}>
                                                {headers.map((_, ci) => (
                                                    <td key={ci}>{row[ci] ?? ''}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        );
                    })() : (
                        <pre className={styles.exportPreviewContent}>{previewText}</pre>
                    )}
                </div>
            )}

            <div className={styles.tableWrap}>
                <table
                    className={styles.table}
                    style={{
                        width: Math.max(760, 280 + users.length * 140 + 120),
                        minWidth: 760,
                    }}
                >
                    <colgroup>
                        <col style={{ width: 280, minWidth: 280 }} />
                        {users.map((u) => (
                            <col key={u.userId} style={{ width: 140 }} />
                        ))}
                        <col style={{ width: 120 }} />
                    </colgroup>
                    <thead>
                        <tr>
                            <th className={`${styles.th} ${styles.itemHeader}`}>
                                {configuredSurveys.length > 0 ? 'Survey / Item' : 'Item'}
                            </th>
                            {users.map((u) => (
                                <th key={u.userId} className={`${styles.th} ${styles.userHeader}`} title={userLabel(u)}>
                                    {canExportGeojson ? (
                                        <div className={styles.cellWrap}>
                                            <button
                                                type="button"
                                                className={styles.headerActionButton}
                                                onMouseEnter={(e) => openGeojsonMenuHover(e, { type: 'col', userId: u.userId })}
                                                onMouseLeave={scheduleGeojsonClose}
                                                onClick={(e) => toggleGeojsonMenuPinned(e, { type: 'col', userId: u.userId })}
                                                aria-label={`GeoJSON actions for ${userLabel(u)}`}
                                            >
                                                {userLabel(u)}
                                            </button>
                                            <span className={styles.cellActions}>
                                                <button
                                                    type="button"
                                                    className={styles.kebabButton}
                                                    onMouseEnter={(e) => openGeojsonMenuHover(e, { type: 'col', userId: u.userId })}
                                                    onMouseLeave={scheduleGeojsonClose}
                                                    onClick={(e) => toggleGeojsonMenuPinned(e, { type: 'col', userId: u.userId })}
                                                    aria-label={`Open GeoJSON menu for ${userLabel(u)}`}
                                                    title="GeoJSON actions"
                                                >
                                                    ⋯
                                                </button>
                                            </span>
                                        </div>
                                    ) : (
                                        <span>{userLabel(u)}</span>
                                    )}
                                </th>
                            ))}
                            <th className={`${styles.th} ${styles.summaryCol}`}>Item progress</th>
                        </tr>
                    </thead>
                    <tbody>
                        {configuredSurveys.map((s) => {
                            const label = surveyScopeLabel(s.type);
                            return (
                                <tr key={`survey-${s.type}`} className={styles.surveyRow}>
                                    <td className={`${styles.td} ${styles.itemCell}`}>
                                        <span className={styles.itemName}>{label}</span>
                                    </td>
                                    {users.map((u) => {
                                        const st = surveyByUser[String(u.userId)]?.[s.type]
                                            || 'not_started';
                                        return (
                                            <td key={`${s.type}-${u.userId}`} className={styles.td}>
                                                <div className={styles.cellWrap}>
                                                    {statusBadge(st)}
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td className={`${styles.td} ${styles.summaryCol}`}>
                                        <span className={styles.summaryText}>
                                            {surveySummary(s.type)}
                                        </span>
                                    </td>
                                </tr>
                            );
                        })}
                        {items.map((it) => {
                            const iid = String(it.itemId);
                            return (
                                <tr key={iid}>
                                    <td className={`${styles.td} ${styles.itemCell}`}>
                                        <button
                                            type="button"
                                            className={styles.itemThumbButton}
                                            onClick={() => handleThumbnailClick(iid)}
                                            aria-label={`Open review viewer for ${it.name}`}
                                        >
                                            <img
                                                src={girderThumbnailUrl(api, iid)}
                                                alt=""
                                                className={styles.itemThumb}
                                                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
                                            />
                                        </button>
                                        <Tooltip text={it.name || ''} position="top" className={styles.itemNameTooltipWrap}>
                                            <span className={styles.itemName}>{it.name}</span>
                                        </Tooltip>
                                        {canExportGeojson ? (
                                            <span className={styles.cellActions}>
                                                <button
                                                    type="button"
                                                    className={styles.kebabButton}
                                                    onMouseEnter={(e) => openGeojsonMenuHover(e, { type: 'row', itemId: iid })}
                                                    onMouseLeave={scheduleGeojsonClose}
                                                    onClick={(e) => toggleGeojsonMenuPinned(e, { type: 'row', itemId: iid })}
                                                    aria-label={`Open GeoJSON menu for item ${it.name}`}
                                                    title="GeoJSON actions"
                                                >
                                                    ⋯
                                                </button>
                                            </span>
                                        ) : null}
                                    </td>
                                    {users.map((u) => {
                                        const status = statusByUserItem[`${u.userId}:${iid}`] || 'not_started';
                                        const notesValue = notesByUserItem[`${u.userId}:${iid}`] || '';
                                        const hasNotes = Boolean(notesValue);
                                        const notesPopoverOpenHere =
                                            notesPopover.open
                                            && String(notesPopover.userId) === String(u.userId)
                                            && String(notesPopover.itemId) === iid;
                                        return (
                                            <td key={`${iid}-${u.userId}`} className={styles.td}>
                                                <div className={styles.cellWrap}>
                                                    {statusBadge(status)}
                                                    {hasNotes ? (
                                                        <Tooltip text="View notes" position="top">
                                                            <button
                                                                type="button"
                                                                className={styles.notesGlyph}
                                                                onClick={(e) => openNotesPopover(e, {
                                                                    userId: u.userId,
                                                                    itemId: iid,
                                                                    value: notesValue,
                                                                    byUser: userLabel(u),
                                                                })}
                                                                aria-label={`View notes by ${userLabel(u)}`}
                                                                aria-expanded={notesPopoverOpenHere}
                                                                aria-haspopup="dialog"
                                                            >
                                                                <span aria-hidden="true">📝</span>
                                                            </button>
                                                        </Tooltip>
                                                    ) : null}
                                                    {canExportGeojson ? (
                                                        <span className={styles.cellActions}>
                                                            <button
                                                                type="button"
                                                                className={styles.kebabButton}
                                                                onMouseEnter={(e) => openGeojsonMenuHover(e, { type: 'cell', itemId: iid, userId: u.userId })}
                                                                onMouseLeave={scheduleGeojsonClose}
                                                                onClick={(e) => toggleGeojsonMenuPinned(e, { type: 'cell', itemId: iid, userId: u.userId })}
                                                                aria-label="Open GeoJSON menu for this cell"
                                                                title="GeoJSON actions"
                                                            >
                                                                ⋯
                                                            </button>
                                                        </span>
                                                    ) : null}
                                                </div>
                                            </td>
                                        );
                                    })}
                                    <td className={`${styles.td} ${styles.summaryCol}`}>
                                        <div className={styles.cellWrap}>
                                            <span className={styles.summaryText}>{itemSummary(iid)}</span>
                                            {canExportGeojson ? (
                                                <span className={styles.cellActions}>
                                                    <button
                                                        type="button"
                                                        className={styles.kebabButton}
                                                        onMouseEnter={(e) => openGeojsonMenuHover(e, { type: 'row', itemId: iid })}
                                                        onMouseLeave={scheduleGeojsonClose}
                                                        onClick={(e) => toggleGeojsonMenuPinned(e, { type: 'row', itemId: iid })}
                                                        aria-label="Open GeoJSON menu for this item row"
                                                        title="GeoJSON actions"
                                                    >
                                                        ⋯
                                                    </button>
                                                </span>
                                            ) : null}
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {users.length > 0 && items.length === 0 && (
                            <tr>
                                <td className={styles.td} colSpan={2 + users.length}>
                                    <span className={styles.muted}>This project has no items.</span>
                                </td>
                            </tr>
                        )}
                        {users.length === 0 && items.length > 0 && (
                            <tr>
                                <td className={styles.td} colSpan={2}>
                                    <span className={styles.muted}>No annotators are assigned to this project.</span>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
                {/* Footer as a separate sticky row so it paints above the table's sticky body column */}
                <div
                    className={styles.tableFooter}
                    style={{
                        gridTemplateColumns: `280px repeat(${users.length}, 140px) 120px`,
                        minWidth: 280 + users.length * 140 + 120,
                    }}
                >
                    <div className={styles.summaryLabel}>
                        {users.length ? (
                            canExportGeojson ? (
                                <button
                                    type="button"
                                    className={styles.headerActionButton}
                                    onMouseEnter={(e) => openGeojsonMenuHover(e, { type: 'all' })}
                                    onMouseLeave={scheduleGeojsonClose}
                                    onClick={(e) => toggleGeojsonMenuPinned(e, { type: 'all' })}
                                >
                                    Overall (per user)
                                </button>
                            ) : (
                                <span>Overall (per user)</span>
                            )
                        ) : ''}
                    </div>
                    {users.map((u) => (
                        <div key={u.userId} className={styles.tableFooterCell}>
                            <div className={styles.cellWrap}>
                                <span className={styles.summaryText}>{userSummary(u.userId)}</span>
                                {canExportGeojson ? (
                                    <span className={styles.cellActions}>
                                        <button
                                            type="button"
                                            className={styles.kebabButton}
                                            onMouseEnter={(e) => openGeojsonMenuHover(e, { type: 'col', userId: u.userId })}
                                            onMouseLeave={scheduleGeojsonClose}
                                            onClick={(e) => toggleGeojsonMenuPinned(e, { type: 'col', userId: u.userId })}
                                            aria-label="Open GeoJSON menu for this user"
                                            title="GeoJSON actions"
                                        >
                                            ⋯
                                        </button>
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    ))}
                    <div className={styles.summaryCol} />
                </div>
            </div>

            <PopoverShell
                open={geojsonMenu.open && canExportGeojson}
                strategy="fixed"
                anchorRect={geojsonMenu.anchorRect}
                placement="bottom-start"
                offset={6}
                pinned={geojsonMenu.pinned}
                layer="secondary"
                onClose={closeGeojsonMenu}
                onMouseEnter={clearGeojsonCloseTimer}
                onMouseLeave={() => { if (!geojsonMenu.pinned) scheduleGeojsonClose(); }}
            >
                {(() => {
                    const scope = geojsonMenu.scope;
                    const ids = collectAnnotationIds(progressPayload, scope || { type: 'all' });
                    const disabled = ids.length === 0;
                    return (
                        <div className={styles.miniMenu}>
                            <div className={styles.miniMenuLabel}>GeoJSON export</div>
                            <div className={styles.miniMenuHint}>{scopeLabel(scope)} ({ids.length} annotation{ids.length === 1 ? '' : 's'})</div>
                            <div className={styles.miniMenuRow}>
                                <button
                                    type="button"
                                    className={styles.miniMenuButton}
                                    disabled={disabled}
                                    onClick={() => { closeGeojsonMenu(); handleGeojsonPreview(scope || { type: 'all' }, 'single'); }}
                                >
                                    Preview (single)
                                </button>
                                <button
                                    type="button"
                                    className={styles.miniMenuButton}
                                    disabled={disabled}
                                    onClick={() => { closeGeojsonMenu(); handleGeojsonDownload(scope || { type: 'all' }, 'single'); }}
                                >
                                    Download (single)
                                </button>
                            </div>
                            <div className={styles.miniMenuRow}>
                                <button
                                    type="button"
                                    className={styles.miniMenuButton}
                                    disabled={disabled}
                                    onClick={() => { closeGeojsonMenu(); handleGeojsonPreview(scope || { type: 'all' }, 'perAnnotation'); }}
                                >
                                    Preview (per-annotation)
                                </button>
                                <button
                                    type="button"
                                    className={styles.miniMenuButton}
                                    disabled={disabled}
                                    onClick={() => { closeGeojsonMenu(); handleGeojsonDownload(scope || { type: 'all' }, 'perAnnotation'); }}
                                >
                                    Download (per-annotation)
                                </button>
                            </div>
                            {disabled && (
                                <div className={styles.miniMenuHint}>
                                    No saved annotations to export in this selection.
                                </div>
                            )}
                        </div>
                    );
                })()}
            </PopoverShell>

            <PopoverShell
                open={wideMenu.open && slcNeedsWidePicker}
                strategy="fixed"
                anchorRect={wideMenu.anchorRect}
                placement="bottom-start"
                offset={6}
                pinned={wideMenu.pinned}
                layer="secondary"
                onClose={closeWideMenu}
                onMouseEnter={clearWideCloseTimer}
                onMouseLeave={() => { if (!wideMenu.pinned) scheduleWideClose(); }}
            >
                <div className={styles.miniMenu}>
                    <div className={styles.miniMenuLabel}>Wide CSV by question</div>
                    <div className={styles.miniMenuHint}>
                        Rows = cases, columns = participants
                    </div>
                    {slcQuestions.map((q, i) => (
                        <button
                            key={String(q.id)}
                            type="button"
                            className={styles.miniMenuButton}
                            style={{ display: 'block', width: '100%', textAlign: 'left', marginTop: i === 0 ? 0 : 4 }}
                            onClick={() => startWideExportForQuestion(q, i)}
                        >
                            {getQuestionDisplayName(q, i)}
                        </button>
                    ))}
                </div>
            </PopoverShell>

            <PopoverShell
                open={notesPopover.open}
                strategy="fixed"
                anchorRect={notesPopover.anchorRect}
                placement="bottom-start"
                offset={6}
                onClose={closeNotesPopover}
                titleId={notesPopoverTitleId}
            >
                <NotesPopoverBody
                    label={notesConfig?.label || 'Notes'}
                    instructions={notesConfig?.instructions || ''}
                    value={notesPopover.value}
                    placeholder=""
                    maxLength={Number.isFinite(notesConfig?.maxLength) ? notesConfig.maxLength : 5000}
                    statusText="Read-only"
                    error={null}
                    isReadOnly
                    showCounter={false}
                    byUser={notesPopover.byUser}
                    titleId={notesPopoverTitleId}
                    instructionsId={notesPopoverDescId}
                />
            </PopoverShell>

        </div>
    );
}

